import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { AccountOp } from '@ambire-common/libs/accountOp/accountOp'
import { Network } from '@ambire-common/interfaces/network'
import { TokenResult } from '@ambire-common/libs/portfolio'
import Text from '@common/components/Text'
import useNavigation from '@common/hooks/useNavigation'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import useBackgroundService from '@web/hooks/useBackgroundService'
import { getUiType } from '@web/utils/uiType'

import BaseTokenItem from './BaseTokenItem'
import RewardsTokenItem from './RewardsTokenItem'

const { isPopup } = getUiType()

export interface TokenRowContextProps {
  /** Read once by the list and passed down, so a row never subscribes to the
   * networks/portfolio controllers itself — see the note in BaseTokenItem. */
  networks: Network[]
  simulatedAccountOp?: AccountOp
}

const TokenItem = ({
  token,
  networks,
  simulatedAccountOp
}: { token: TokenResult } & TokenRowContextProps) => {
  const { t } = useTranslation()
  const { dispatch } = useBackgroundService()
  const { navigate } = useNavigation()

  // These three depend only on the token's own `rewardsType` flag — no networks,
  // no simulation, no formatting. Deriving them here instead of calling
  // `getAndFormatTokenDetails` saves a second full format pass per row (the row
  // itself already runs one), which is the difference between one and two
  // `Intl.NumberFormat` batches for every token in the list.
  const { rewardsType } = token.flags
  const isRewards = rewardsType === 'wallet-rewards'
  const isVesting = rewardsType === 'wallet-vesting'
  const isProjectedRewards = rewardsType === 'wallet-projected-rewards'

  const handleDetailsPress = useCallback(() => {
    navigate(WEB_ROUTES.rewards)
  }, [navigate])

  const projectedRewardsDescription = useMemo(
    () => (
      <Text fontSize={12} weight="regular">
        {t('Projected Rewards')}
      </Text>
    ),
    [t]
  )

  const sendTransaction = useCallback(
    (type: 'claimWalletRequest' | 'mintVestingRequest') => {
      dispatch({
        type: 'REQUESTS_CONTROLLER_BUILD_REQUEST',
        params: { type, params: { token } }
      })
    },
    [dispatch, token]
  )

  if (isProjectedRewards)
    return (
      <RewardsTokenItem
        onPress={handleDetailsPress}
        token={token}
        networks={networks}
        simulatedAccountOp={simulatedAccountOp}
        description={projectedRewardsDescription}
      />
    )
  if (isRewards)
    return (
      <RewardsTokenItem
        token={token}
        networks={networks}
        simulatedAccountOp={simulatedAccountOp}
        onPress={() => sendTransaction('claimWalletRequest')}
        actionButtonText="Claim"
        description="Claimable rewards"
      />
    )
  if (isVesting)
    return (
      <RewardsTokenItem
        token={token}
        networks={networks}
        simulatedAccountOp={simulatedAccountOp}
        actionButtonText="Claim"
        onPress={() => sendTransaction('mintVestingRequest')}
        description={!isPopup ? 'Claimable early supporters vestings' : 'Claimable vestings'}
      />
    )

  return (
    <BaseTokenItem token={token} networks={networks} simulatedAccountOp={simulatedAccountOp} />
  )
}

export default React.memo(TokenItem)
